import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { api } from '../../lib/api'
import { t } from '../../lib/i18n'

export default function SignIn() {
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/me'

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [bannerError, setBannerError] = useState<string | null>(null)

  // Resolve `next` to an absolute URL anchored at the SPA's current origin so
  // the API redirects back to the same Pages preview subdomain (the API
  // re-validates with safeNextUrl). `new URL(next, origin)` returns `next`
  // untouched if it's already absolute.
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://kindred-web.pages.dev'
  const nextAbsolute = new URL(next, origin).toString()

  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api'
  const googleHref = `${apiBase}/auth/google/start?next=${encodeURIComponent(nextAbsolute)}`

  function validateEmail(raw: string): string | null {
    const v = raw.trim()
    if (!v) return 'Adresse email requise.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Adresse email invalide.'
    return null
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBannerError(null)

    const maybe = validateEmail(email)
    if (maybe) {
      setFieldError(maybe)
      return
    }

    setFieldError(null)
    setStatus('submitting')
    try {
      await api.authMagicLinkRequest(email.trim(), nextAbsolute)
      setStatus('sent')
    } catch {
      setStatus('idle')
      setBannerError('Erreur réseau. Réessayez.')
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <div className="auth-card">
          {status === 'sent' ? (
            <div className="sent">
              <div className="sent-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48" width="48" height="48">
                  <rect
                    x="6"
                    y="12"
                    width="36"
                    height="26"
                    rx="3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M6 14 L24 28 L42 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="eyebrow">Email envoyé</p>
              <h1 className="auth-title">Vérifiez votre boîte mail</h1>
              <p className="auth-sub">
                Si <strong>{email.trim()}</strong> est associé à un compte, un lien de connexion
                vous a été envoyé.
              </p>
              <p className="auth-fine">
                Pensez à regarder dans vos courriers indésirables. Le lien est valable 30 minutes.
              </p>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setStatus('idle')
                  setEmail('')
                }}
              >
                ← Utiliser une autre adresse
              </button>
            </div>
          ) : (
            <>
              <p className="eyebrow">Bienvenue</p>
              <h1 className="auth-title">{t('auth.sign_in.heading')}</h1>
              <p className="auth-sub">Connectez-vous pour gérer votre liste de naissance.</p>

              {bannerError && (
                <div role="alert" className="banner banner-error">
                  {bannerError}
                </div>
              )}

              <form className="auth-form" onSubmit={onSubmit} noValidate>
                <label className="field">
                  <span className="lbl">Adresse email</span>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    className={`input ${fieldError ? 'input-error' : ''}`}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setFieldError(null)
                    }}
                    placeholder={t('auth.sign_in.email_placeholder')}
                    disabled={status === 'submitting'}
                    aria-invalid={fieldError ? true : undefined}
                    aria-describedby={fieldError ? 'email-err' : undefined}
                  />
                  {fieldError && (
                    <span id="email-err" className="field-error">
                      {fieldError}
                    </span>
                  )}
                </label>

                <button
                  type="submit"
                  className="btn-primary btn-full"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? (
                    <span className="btn-spin">
                      <span className="mini-spin" aria-hidden="true" /> Envoi en cours…
                    </span>
                  ) : (
                    t('auth.sign_in.send_link')
                  )}
                </button>
              </form>

              <div className="or">
                <span>ou</span>
              </div>

              <button
                type="button"
                className="btn-secondary btn-full"
                disabled={status === 'submitting'}
                onClick={() => {
                  window.location.href = googleHref
                }}
              >
                {t('auth.sign_in.or_google')}
              </button>

              <p className="auth-fine">
                En continuant, vous acceptez nos <a href="/terms">conditions</a> et notre{' '}
                <a href="/privacy">politique de confidentialité</a>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
